import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { useMediaQuery } from "react-responsive"
import { useRecoilState } from "recoil"
import Carousel from "react-multi-carousel"
import { useLocation, useNavigate } from "react-router-dom"

import MainPhoto from "src/assets/imgs/main.png"
import MapPhoto from "src/assets/imgs/map.png"
import CounselingPhoto from "src/assets/imgs/top_counseling.png"
import { NEWS_INTERVIEW_TYPES_STRING, RESPONSE_CODE, SORT_ORDER } from "src/modules/const"
import {
  Event,
  EventService,
  Job,
  JobService,
  News,
  NewsService,
  Product,
  ProductService,
  Sponsor,
  SponsorService,
} from "src/openapi"
import { ShowHeader } from "src/states/showHeader"
import { Box, Button, Container, Grid, Image, Typography, ViewMore } from "src/UILibrary"
import { EventJoinModal } from "../event/components/EventJoinModal"
import { NewsItem } from "../news/components/NewsItem"
import { EventItem } from "./components/EventItem"
import { IncubationCafeItem } from "./components/IncubationCafeItem"
import { InterviewItem } from "./components/InterviewItem"
import { JobItem } from "./components/JobItem"
import { OfficeItem } from "./components/OfficeItem"
import { ServiceItem } from "./components/ServiceItem"
import { SponsorItem } from "./components/SponsorItem"
import "./top.scss"
import "react-multi-carousel/lib/styles.css"

const responsive = {
  tablet: {
    breakpoint: { max: 1090, min: 700 },
    items: 2.5,
  },
  mobile: {
    breakpoint: { max: 700, min: 0 },
    items: 1.3,
  },
}

export const Top = () => {
  const { t } = useTranslation("translation", { keyPrefix: "top" })
  const isPC = useMediaQuery({ query: "(min-width: 1050px)" })
  const isLargePC = useMediaQuery({ query: "(min-width: 1085px)" })

  const [, setShowHeader] = useRecoilState(ShowHeader)

  const [events, setEvents] = useState<Event[]>([])
  const [interviews, setInterviews] = useState<News[]>([])
  const [news, setNews] = useState<News[]>([])
  const [serviceIntroductions, setServiceIntroductions] = useState<Product[]>([])
  const [jobOffers, setJobOffers] = useState<Job[]>([])
  const [sponsers, setSponsers] = useState<Sponsor[]>([])

  const [openJoinModal, setOpenJoinModal] = useState<boolean>(false)
  const [selectedEvent, setSelectedEvent] = useState<Event>()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    setShowHeader(true)
  }, [setShowHeader])

  useEffect(() => {
    if (location.hash) {
      let elem = document.getElementById(location.hash.slice(1))
      if (elem) {
        elem.scrollIntoView({ behavior: "smooth" })
      }
    }
  }, [location])

  const refreshEvent = () => {
    EventService.listEvent(
      undefined,
      0,
      4,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      `${dayjs().format("YYYY-MM-DD")},${dayjs().add(1, "year").format("YYYY-MM-DD")}`,
      SORT_ORDER.ASCENDING,
      "start_time"
    ).then((res) => {
      if (res.status === RESPONSE_CODE.SUCCESS) {
        setEvents(res.data?.events as Event[])
      }
    })
  }

  useEffect(() => {
    refreshEvent()

    NewsService.newsList(
      undefined,
      0,
      4,
      undefined,
      NEWS_INTERVIEW_TYPES_STRING.INTERVIEW,
      undefined,
      SORT_ORDER.DESCENDONG,
      "created_at"
    ).then((res) => {
      if (res.status === RESPONSE_CODE.SUCCESS) {
        setInterviews(res.data?.news as News[])
      }
    })

    NewsService.newsList(
      undefined,
      0,
      5,
      undefined,
      NEWS_INTERVIEW_TYPES_STRING.NEWS,
      undefined,
      SORT_ORDER.DESCENDONG,
      "created_at"
    ).then((res) => {
      setNews(res.data?.news as News[])
    })

    ProductService.productList(0, 4).then((res) => {
      if (res.status === RESPONSE_CODE.SUCCESS) {
        setServiceIntroductions(res.data?.products as Product[])
      }
    })

    JobService.jobList(0, 4).then((res) => {
      if (res.status === RESPONSE_CODE.SUCCESS) {
        setJobOffers(res.data?.jobs as Job[])
      }
    })

    SponsorService.sponsorList(0, 15).then((res) => {
      if (res.status === RESPONSE_CODE.SUCCESS) {
        setSponsers(res.data?.sponsors as Sponsor[])
      }
    })
  }, [])

  const handleEventSelected = (event: Event) => {
    setSelectedEvent(event)
    setOpenJoinModal(true)
  }

  return (
    <Box className="main-body-with-footer">
      <Container maxWidth="lg" className="header-container">
        <Box className="banner">
          {isPC && (
            <Box maxWidth={375} mt="40px" flexShrink={0} mr="30px" position="relative">
              <Typography.Extreme color="textPrimary">{t("in_takaoka")}</Typography.Extreme>
              <Typography.Extreme color="primary" fontWeight={500}>
                {t("start_business")}
                <span style={{ display: "inline-block" }} color="textPrimary">
                  {t("do")}
                </span>
              </Typography.Extreme>
              <Typography.SubTitle
                fontWeight={700}
                mt="30px"
                sx={{ fontFamily: "Noto Sans JP !important" }}
              >
                {t("main_description")}
              </Typography.SubTitle>
              <Image
                src={MapPhoto}
                sx={{ position: "absolute", top: "260px", left: "113px", width: "280px" }}
              />
            </Box>
          )}

          <Image src={MainPhoto} className="main-img" imageStyle={{ width: "100%" }} />
          {!isPC && <Image src={MapPhoto} className="map-img" />}
          {!isPC && (
            <Box mt="80px" flexShrink={0} px="20px" position="relative">
              <Typography.Extreme color="textPrimary">{t("in_takaoka")}</Typography.Extreme>
              <Typography.Extreme color="primary" fontWeight={500}>
                {t("start_business")}
                <Typography.Extreme display="inline-block" color="textPrimary">
                  {t("do")}
                </Typography.Extreme>
              </Typography.Extreme>
              <Typography.SubTitle
                fontWeight={700}
                mt="30px"
                sx={{ fontFamily: "Noto Sans JP !important" }}
              >
                {t("main_description")}
              </Typography.SubTitle>
            </Box>
          )}
        </Box>

        {/* 相談員予約 */}
        <Box display="flex" alignItems="center" flexDirection="column" mt="120px">
          <Typography.Large color="textPrimary" mb="20px">
            {t("counseling_appeal")}
          </Typography.Large>
          <Button
            variant="contained"
            sx={{
              minWidth: "160px",
              mt: "20px",
              mb: "20px",
              px: "40px",
              py: "20px",
              fontSize: "1rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: "1rem",
              borderRadius: "0px",
              ":hover": {
                bgcolor: "#2DA389",
              },
            }}
            onClick={() => navigate("/adviser")}
          >
            {t("counseling_btn")}
          </Button>
          <Box mt="30px" mb="10px">
            <Box mb="10px">{t("counseling_info")}</Box>
            <Box mb="10px">{t("counseling_place")}</Box>
            <Box>{t("counseling_tel")}</Box>
          </Box>
          <Image src={CounselingPhoto} imageStyle={{ width: "100%" }} />
        </Box>

        {/* event/seminars */}
        <Box className="event-container">
          <Box display={isPC ? "flex" : "block"} alignItems="center">
            <Box mr={5}>
              <Typography.Larger
                textTransform="uppercase"
                color="textPrimary"
                className="top-title"
              >
                events/seminars
              </Typography.Larger>
              {isPC && (
                <Typography.Large mt="20px" color="textPrimary">
                  {t("event_seminar")}
                </Typography.Large>
              )}
            </Box>
            <ViewMore to="/event" sx={{ ":hover": { opacity: 0.8, cursor: "pointer" } }}>
              {t("list_of_event_seminar")}
            </ViewMore>
          </Box>
          {events.length === 0 ? (
            <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
              <Typography.SubTitleNormal
                sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
              >
                {t("no_data")}
              </Typography.SubTitleNormal>
            </Box>
          ) : (
            <>
              {isLargePC ? (
                <Grid container spacing="10px">
                  {events.map((event, index) => {
                    return (
                      <Grid item xs={6} md={3} key={index}>
                        <EventItem item={event} handleSelected={handleEventSelected} />
                      </Grid>
                    )
                  })}
                </Grid>
              ) : (
                <Carousel
                  responsive={responsive}
                  removeArrowOnDeviceType={["desktop", "tablet", "mobile"]}
                >
                  {events.map((event, index) => {
                    return (
                      <EventItem key={index} item={event} handleSelected={handleEventSelected} />
                    )
                  })}
                </Carousel>
              )}
            </>
          )}
        </Box>
      </Container>
      {/* news */}
      <Container maxWidth="lg" sx={{ mt: "60px" }}>
        <Box display={isPC ? "flex" : "block"} alignItems="center">
          <Box mr={5}>
            <Typography.Larger textTransform="uppercase" color="textPrimary" className="top-title">
              News
            </Typography.Larger>
            {isPC && (
              <Typography.Large mt={2.5} color="textPrimary">
                {t("new_arrival_news")}
              </Typography.Large>
            )}
          </Box>
          <ViewMore to="/news" sx={{ ":hover": { opacity: 0.8, cursor: "pointer" } }}>
            {t("news_list")}
          </ViewMore>
        </Box>
      </Container>
      {news.length === 0 ? (
        <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
          <Typography.SubTitleNormal sx={{ mx: "auto" }}>{t("no_data")}</Typography.SubTitleNormal>
        </Box>
      ) : (
        <Grid container maxWidth="lg" mx="auto" mt="40px">
          {news.length > 0 && (
            <Grid item md={6} sm={12} xs={12}>
              <NewsItem item={news[0]} large />
            </Grid>
          )}

          <Grid item md={6} sm={12} xs={12}>
            <Grid container minHeight={150}>
              {news.length >= 2 && (
                <Grid item md={6} sm={6} xs={6}>
                  <NewsItem item={news[1]} />
                </Grid>
              )}
              {news.length >= 3 && (
                <Grid item md={6} sm={6} xs={6}>
                  <NewsItem item={news[2]} />
                </Grid>
              )}
            </Grid>
            <Grid container minHeight={150}>
              {news.length >= 4 && (
                <Grid item md={6} sm={6} xs={6}>
                  <NewsItem item={news[3]} />
                </Grid>
              )}
              {news.length >= 5 && (
                <Grid item md={6} sm={6} xs={6}>
                  <NewsItem item={news[4]} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
      <Container maxWidth="lg" id="download">
        <Box mt="60px">
          <Box>
            <Typography.Larger className="top-title" textTransform="uppercase" color="textPrimary">
              {t("download_title")}
            </Typography.Larger>
            <Button
              variant="contained"
              sx={{
                minWidth: "160px",
                mt: "20px",
                py: "10px",
                fontSize: "1rem",
                fontFamily: "Noto Sans JP !important",
                lineHeight: "1rem",
                borderRadius: "0px",
                ":hover": {
                  bgcolor: "#2DA389",
                },
              }}
              onClick={() => {
                window.location.href =
                  "https://www.dropbox.com/scl/fo/4x076v7p276qndaa6lo18/h?rlkey=9eawjf8lgihx7upa61z3onu0v&dl=0"
              }}
            >
              {t("download_pdf")}
            </Button>
          </Box>
        </Box>
      </Container>
      <Box mt="20px">
        <Typography.Larger
          lineHeight={3.75}
          className="top-title"
          textTransform="uppercase"
          textAlign="center"
        >
          incubation cafe
        </Typography.Larger>
        <Box
          sx={{
            py: "40px",
            position: "relative",
            background:
              "linear-gradient(99.46deg, rgba(15, 124, 158, 0.6) -0.55%, rgba(14, 140, 80, 0.6) 100%)",
          }}
        >
          <Image
            src={`${process.env.REACT_APP_S3_URL}bg_1.jpeg`}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: -1,
              height: "100%",
              width: "100%",
            }}
            imageStyle={{
              height: "100%",
              width: "100%",
            }}
          />
          {isPC ? (
            <Grid container className="incubation-grid" spacing="20px">
              <Grid item md={12}>
                <IncubationCafeItem
                  image={`${process.env.REACT_APP_S3_URL}lounge.jpg`}
                  title={t("share_lounge")}
                  to="https://tsutaya.tsite.jp/store/lounge/detail/3233/"
                  newTab
                />
              </Grid>
              <Grid item md={isPC ? 6 : 12}>
                <IncubationCafeItem
                  image={`${process.env.REACT_APP_S3_URL}meeting_room_C.jpg`}
                  title={t("study_space")}
                  to="/space_meeting"
                />
              </Grid>
              <Grid item md={isPC ? 6 : 12}>
                <IncubationCafeItem
                  image={`${process.env.REACT_APP_S3_URL}book_library1.jpg`}
                  title={t("book_library")}
                  to="/book_library"
                />
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ width: "100%" }}>
              <IncubationCafeItem
                image={`${process.env.REACT_APP_S3_URL}lounge.jpg`}
                title={t("share_lounge")}
                to="https://tsutaya.tsite.jp/store/lounge/detail/3233/"
                newTab
              />
              <IncubationCafeItem
                image={`${process.env.REACT_APP_S3_URL}meeting_room_C.jpg`}
                title={t("study_space")}
                to="/space_meeting"
              />
              <IncubationCafeItem
                image={`${process.env.REACT_APP_S3_URL}book_library1.jpg`}
                title={t("book_library")}
                to="/book_library"
              />
            </Box>
          )}
        </Box>
      </Box>
      {/* interview */}
      <Box mt="60px" pt="60px" pb="40px">
        <Container maxWidth="lg">
          <Box alignItems="center" display={isPC ? "flex" : "block"}>
            <Box mr={5}>
              <Typography.Larger
                textTransform="uppercase"
                color="textPrimary"
                className="top-title"
              >
                {t("entrepreneur")}interview
              </Typography.Larger>
              {isPC && (
                <Typography.Large mt={2.5} color="textPrimary">
                  {t("interview")}
                </Typography.Large>
              )}
            </Box>
            <ViewMore to="/news" sx={{ ":hover": { opacity: 0.8, cursor: "pointer" } }}>
              {t("entrepreneur_interview_list")}
            </ViewMore>
          </Box>
          {interviews.length === 0 ? (
            <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
              <Typography.SubTitleNormal
                sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
              >
                {t("no_data")}
              </Typography.SubTitleNormal>
            </Box>
          ) : (
            <>
              {isLargePC ? (
                <Grid container>
                  {interviews.map((interview, index) => {
                    return (
                      <Grid item xs={6} md={3} key={index}>
                        <InterviewItem item={interview} />
                      </Grid>
                    )
                  })}
                </Grid>
              ) : (
                <Carousel
                  responsive={responsive}
                  removeArrowOnDeviceType={["desktop", "tablet", "mobile"]}
                >
                  {interviews.map((interview, index) => {
                    return <InterviewItem key={index} item={interview} />
                  })}
                </Carousel>
              )}
            </>
          )}
        </Container>
      </Box>
      <Container maxWidth="lg">
        <Box mt="60px">
          <Box display={isPC ? "flex" : "block"} alignItems="center">
            <Box mr={5}>
              <Typography.Larger
                textTransform="uppercase"
                className="top-title"
                color="textPrimary"
              >
                SERVICE INTRODUCTION
              </Typography.Larger>
              {isPC && (
                <Typography.Large mt={2.5} color="textPrimary">
                  {t("service_introduction")}
                </Typography.Large>
              )}
            </Box>
            <ViewMore
              to="/service_introduction"
              sx={{ ":hover": { opacity: 0.8, cursor: "pointer" } }}
            >
              {t("list_of_service_introduction")}
            </ViewMore>
          </Box>
          {serviceIntroductions.length === 0 ? (
            <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
              <Typography.SubTitleNormal
                sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
              >
                {t("no_data")}
              </Typography.SubTitleNormal>
            </Box>
          ) : (
            <>
              {isLargePC ? (
                <Grid container>
                  {serviceIntroductions.map((service, index) => {
                    return (
                      <Grid item xs={6} md={3} key={index}>
                        <ServiceItem item={service} />
                      </Grid>
                    )
                  })}
                </Grid>
              ) : (
                <Carousel
                  responsive={responsive}
                  removeArrowOnDeviceType={["desktop", "tablet", "mobile"]}
                >
                  {serviceIntroductions.map((service, index) => {
                    return <ServiceItem key={index} item={service} />
                  })}
                </Carousel>
              )}
            </>
          )}
        </Box>
      </Container>
      <Container maxWidth="lg">
        <Box mt="60px">
          <Box display={isPC ? "flex" : "block"} alignItems="center">
            <Box mr={5}>
              <Typography.Larger
                className="top-title"
                textTransform="uppercase"
                color="textPrimary"
              >
                JOB OFFER
              </Typography.Larger>
              {isPC && (
                <Typography.Large mt={2.5} color="textPrimary">
                  {t("job_offer")}
                </Typography.Large>
              )}
            </Box>
            <ViewMore to="/job_offer" sx={{ ":hover": { opacity: 0.8, cursor: "pointer" } }}>
              {t("list_of_job_offer")}
            </ViewMore>
          </Box>
          {jobOffers.length === 0 ? (
            <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
              <Typography.SubTitleNormal
                sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
              >
                {t("no_data")}
              </Typography.SubTitleNormal>
            </Box>
          ) : (
            <>
              {isLargePC ? (
                <Grid container>
                  {jobOffers.map((offer, index) => {
                    return (
                      <Grid item xs={6} md={3} key={index}>
                        <JobItem item={offer} />
                      </Grid>
                    )
                  })}
                </Grid>
              ) : (
                <Carousel
                  responsive={responsive}
                  removeArrowOnDeviceType={["desktop", "tablet", "mobile"]}
                >
                  {jobOffers.map((offer, index) => {
                    return <JobItem item={offer} key={index} />
                  })}
                </Carousel>
              )}
            </>
          )}
        </Box>
      </Container>
      {/* sponsors */}
      <Box mt="60px" py="60px">
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center">
            <Box mr={5}>
              <Typography.Larger className="top-title">{t("sponsors")}</Typography.Larger>
            </Box>
          </Box>
          {sponsers.length === 0 ? (
            <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
              <Typography.SubTitleNormal
                sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
              >
                {t("no_data")}
              </Typography.SubTitleNormal>
            </Box>
          ) : (
            <Grid container>
              {sponsers.map((sponser, index) => {
                return (
                  <Grid item md={2.4} xs={6} key={index}>
                    <SponsorItem item={sponser} />
                  </Grid>
                )
              })}
            </Grid>
          )}
        </Container>
      </Box>
      <Container maxWidth="lg" sx={isPC ? { px: "3.125rem", mb: "60px" } : {}}>
        <Box mt="60px">
          <Box display="flex" alignItems="center">
            <Box mr={5}>
              <Typography.Larger
                className="top-title"
                textTransform="uppercase"
                color="textPrimary"
              >
                {t("access")}
              </Typography.Larger>
            </Box>
          </Box>
          <Container maxWidth="md" sx={{ mx: "auto", my: "20px", px: "0px !important" }}>
            <OfficeItem
              title="高岡まちなかスタートアップ支援施設「TASU」"
              address="富山県高岡市御旅屋町101番地 御旅屋セリオ４階"
              operatingTime="（平日）13:00～21:00（土日祝日）10:00～18:00"
              breakTime="毎週水曜日、年末年始"
              support="起業・創業から事業承継まで、幅広く相談ができます。"
              mapURL="https://maps.google.com/maps?q=36.7455433,137.0139992&hl=ja&z=14&output=embed"
            />
            {/* <OfficeItem
              title="TSUTAYA BOOKSTORE 金沢エムザ"
              address="石川県金沢市武蔵町15番1号金沢エムザ3F 御旅屋セリオ４階"
              operatingTime="10:00～19:00"
              breakTime="なし"
              support="常時2名以上の体制で、起業・創業から事業承継まで、幅広く相談ができます。"
            /> */}
          </Container>
        </Box>
      </Container>

      <EventJoinModal
        open={openJoinModal}
        handleOpen={setOpenJoinModal}
        item={selectedEvent}
        refreshEvents={refreshEvent}
      />
    </Box>
  )
}
